import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgxSpinner, NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { Observable, Subject, takeUntil } from 'rxjs';
import { SORT_OPTIONS } from '../../_shared/_constants/sort-options';
import { IShopifyProduct, IProductType, IVendor } from '@app/_shared/_models';
import { CollectionService } from '../../_shared/_services/collection.service';
import { ProductTypeService } from '../../_shared/_services/product-type.service';
import { ProductService } from '../../_shared/_services/product.service';
import { VendorService } from '../../_shared/_services/vendor.service';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { ProductCardComponent } from '../../_shared/_components/product-card/product-card.component';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    imports: [MatFormField, MatSelect, FormsModule, ReactiveFormsModule, MatOption, NgxSpinnerComponent, ProductCardComponent, RouterLink, MatIcon, AsyncPipe]
})
export class SearchComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly collectionService = inject(CollectionService);
  private readonly ngxSpinnerService = inject(NgxSpinnerService);
  private readonly productService = inject(ProductService);
  private readonly vendorService = inject(VendorService);
  private readonly productTypeService = inject(ProductTypeService);

  public keyword = '';
  public products$!: Observable<Array<IShopifyProduct>>;
  public spinner$!: Observable<NgxSpinner>;
  public allProducts = new Array<IShopifyProduct>();
  public filteredProducts = new Array<IShopifyProduct>();
  public vendors = new Array<IVendor>();
  public vendorsControl = new UntypedFormControl();
  public categories = new Array<IProductType>();
  public categoriesControl = new UntypedFormControl();
  public sortOptions = [...SORT_OPTIONS, 'Most Relevant'];
  public sortControl = new UntypedFormControl();
  public selectedSortOption = this.sortOptions[6];
  public selectedVendorOption = new Array<IVendor>();
  public selectedCategoryOption = new Array<IProductType>();

  private readonly unsubscribe: Subject<undefined> = new Subject<undefined>();

  constructor() {
    this.products$ = this.collectionService.products$;
  }

  async ngOnInit(): Promise<void> {
    this.spinner$ = this.ngxSpinnerService.spinnerObservable;
    await this.ngxSpinnerService.show();

    this.categories = this.productTypeService.productTypes;
    this.vendors = this.vendorService.vendors;
    this.sortControl.setValue(this.selectedSortOption);

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (params: any) => {
        this.keyword = params['search'];
        if (this.keyword) {
          this.collectionService.searchProductsByKeyword(this.keyword);
        }
        await this.ngxSpinnerService.hide();
    });

    this.products$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (products: Array<IShopifyProduct>) => {
        if (products?.length > 0) {
          this.allProducts = ProductService.sortProducts(products, this.selectedSortOption, this.keyword);
          this.filteredProducts = ProductService.sortProducts(products, this.selectedSortOption, this.keyword);

          this.categoriesControl.valueChanges
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((categories: Array<IProductType>) => {
              if (categories?.length < 1) {
                this.filteredProducts = ProductService.filterByVendors(this.allProducts, this.vendorsControl.value);
              } else {
                this.filteredProducts = ProductService.filterByVendors(this.allProducts, this.vendorsControl.value);
                this.filteredProducts = ProductService.filterByProductType(this.filteredProducts, categories);
              }
              this.filteredProducts = ProductService.sortProducts(this.filteredProducts, this.sortControl.value);
            });
    
        this.sortControl.valueChanges
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((sortOption: string) => {
            this.filteredProducts = ProductService.sortProducts(this.filteredProducts, sortOption, this.keyword);
          });
    
        this.vendorsControl.valueChanges
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((vendors: Array<IVendor>) => {
            if (vendors?.length < 1) {
              this.filteredProducts = ProductService.filterByProductType(this.allProducts, this.categoriesControl.value);
            } else {
              this.filteredProducts = ProductService.filterByProductType(this.allProducts, this.categoriesControl.value);
              this.filteredProducts = ProductService.filterByVendors(this.filteredProducts, vendors);
            }
            this.filteredProducts = ProductService.sortProducts(this.filteredProducts, this.sortControl.value);
          });

          await this.ngxSpinnerService.hide();
        } else {
          this.filteredProducts = [];
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  public compareVendors(object1: IVendor, object2: IVendor): boolean {
    return object1 && object2 && object1.name == object2.name;
  }

  public compareCategories(object1: IProductType, object2: IProductType): boolean {
    return object1 && object2 && object1.label == object2.label;
  }

  public clearFilters(): void {
    this.categoriesControl.setValue([]);
    this.vendorsControl.setValue([]);
  }
}
